import client from './client';
import type { PracticeProfile, StaffProvisioningRequest } from '../types/api';

export const customersApi = {
    /**
     * List all practices for a customer
     */
    listPractices: async (customerId: string): Promise<PracticeProfile[]> => {
        const response = await client.get('/api/v1/customers/practices', {
            params: { customer_id: customerId }
        });
        return response.data;
    },

    /**
     * Add a new practice
     */
    addPractice: async (practice_data: Partial<PracticeProfile>): Promise<PracticeProfile> => {
        const response = await client.post<PracticeProfile>('/api/v1/customers/practices', practice_data);
        return response.data;
    },

    async updatePractice(practice_id: string, practice_data: Partial<PracticeProfile>): Promise<PracticeProfile> {
        const response = await client.put<PracticeProfile>(`/api/v1/customers/practices/${practice_id}`, practice_data);
        return response.data;
    },

    async deletePractice(practice_id: string): Promise<void> {
        await client.delete(`/api/v1/customers/practices/${practice_id}`);
    },

    async savePracticeCredentials(practice_id: string, credentials: any): Promise<void> {
        await client.post(`/api/v1/customers/practices/${practice_id}/credentials`, credentials);
    },

    /**
     * Provision a staff member with a billing key
     */
    provisionStaff: async (req: StaffProvisioningRequest): Promise<{ message: string, data: any }> => {
        const response = await client.post('/api/v1/customers/staff', req);
        return response.data;
    },

    /**
     * List all staff members for the current customer
     */
    listStaff: async (): Promise<any[]> => {
        const response = await client.get('/api/v1/customers/staff');
        return response.data;
    },

    /**
     * Toggle a staff member's status (Active vs Disabled)
     */
    toggleStaffStatus: async (email: string, status: 'Active' | 'Disabled'): Promise<{ message: string }> => {
        const response = await client.put('/api/v1/customers/staff/status', { email, status });
        return response.data;
    },

    /**
     * Cancel a pending staff invitation
     */
    cancelStaffInvitation: async (email: string): Promise<{ message: string }> => {
        const response = await client.delete(`/api/v1/customers/staff/invitations/${encodeURIComponent(email)}`);
        return response.data;
    },

    /**
     * Update roles and practices for an existing staff member
     */
    updateStaffAssignments: async (email: string, assignments: { is_owner_admin: boolean; is_customer_admin: boolean; is_staff: boolean; practice_ids: string[] }): Promise<{ message: string }> => {
        const response = await client.put(`/api/v1/customers/staff/${encodeURIComponent(email)}`, assignments);
        return response.data;
    },

    /**
     * Get organization settings
     */
    getOrganizationSettings: async (): Promise<any> => {
        const response = await client.get('/api/v1/customers/organization');
        return response.data;
    },

    /**
     * Update organization settings
     */
    updateOrganizationSettings: async (settings: any): Promise<any> => {
        const response = await client.put('/api/v1/customers/organization', settings);
        return response.data;
    },
};
